import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";

interface AuthState {
  username: string;
  email: string;
  groups: string[];
  isAdmin: boolean;
  idToken: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, "refresh" | "logout">>({
    username: "",
    email: "",
    groups: [],
    isAdmin: false,
    idToken: null,
    loading: true,
  });

  const refresh = async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload ?? {};
      const groups = (payload["cognito:groups"] as string[]) ?? [];

      setState({
        username: user.username,
        email: (payload.email as string) ?? "",
        groups,
        isAdmin: groups.includes("admin"),
        idToken: session.tokens?.idToken?.toString() ?? null,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const logout = async () => {
    await signOut();
    window.location.reload();
  };

  useEffect(() => { refresh(); }, []);

  return (
    <AuthContext.Provider value={{ ...state, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
